import { FC } from 'react';

import { Flex, FlexProps, Skeleton } from '@mantine/core';

/**
 * SkeletonList Component
 *
 * A reusable component that renders a list of skeleton loaders. It extends the
 * Mantine `Flex` component, allowing you to pass any valid `Flex` props.
 *
 * @param {number} items - The number of skeleton items to display. Default is 5.
 * @param {string} itemHeight - The height of each skeleton item. Default is '50px'.
 * @param {FlexProps} rest - Additional props passed down to the underlying Flex component.
 *
 * @example
 * // Default usage:
 * <SkeletonList />
 *
 * @example
 * // Custom number of items and item height:
 * <SkeletonList items={10} itemHeight="70px" />
 *
 * @example
 * // Extending Flex props:
 * <SkeletonList items={3} itemHeight="40px" direction="column" gap="md" />
 */
interface SkeletonListProps extends FlexProps {
  items?: number;
  itemHeight?: string;
}

const SkeletonList: FC<SkeletonListProps> = ({ items = 5, itemHeight = '50px', ...rest }) => {
  return (
    <Flex gap="sm" direction="column" {...rest}>
      {Array.from({ length: items }).map((_, index) => (
        <Skeleton key={index} height={itemHeight} width="100%" />
      ))}
    </Flex>
  );
};

export default SkeletonList;
